import React from 'react';
import { Input } from './ui/input';
import MaskedInput from './ui/MaskedInput';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';

const PrincipalStep = ({ formData, setFormData }) => {
  const addPrincipal = () => {
    const newPrincipal = {
      id: `principal-${Date.now()}`,
      firstName: '',
      lastName: '',
      title: '',
      nationality: 'United States',
      ssn: '',
      dayOfBirth: '',
      driverLicense: { number: '', expiration: '', state: '' },
      address: { street: '', city: '', zip: '', state: '', country: 'United States' },
      phone: '',
      email: '',
      ownershipPercentage: 0,
      isSigner: 'No',
      isPersonalGuarantee: 'No',
      isManagement: 'Yes'
    };
    
    setFormData(prev => ({
      ...prev,
      principals: [...(prev.principals || []), newPrincipal]
    }));
  };

  const updatePrincipal = (index, field, value) => {
    setFormData(prev => {
      const principals = [...(prev.principals || [])];
      principals[index] = { ...principals[index], [field]: value };
      return { ...prev, principals };
    });
  };

  const updatePrincipalAddress = (index, field, value) => {
    setFormData(prev => {
      const principals = [...(prev.principals || [])];
      principals[index] = {
        ...principals[index],
        address: { ...principals[index].address, [field]: value }
      };
      return { ...prev, principals };
    });
  };

  const updatePrincipalDriverLicense = (index, field, value) => {
    setFormData(prev => {
      const principals = [...(prev.principals || [])];
      principals[index] = {
        ...principals[index],
        driverLicense: { ...principals[index].driverLicense, [field]: value }
      };
      return { ...prev, principals };
    });
  };

  const removePrincipal = (index) => {
    setFormData(prev => ({
      ...prev,
      principals: prev.principals.filter((_, i) => i !== index)
    }));
  };

  const principals = formData.principals || [];

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-slate-100">Principal Information</h3>
        <Button onClick={addPrincipal} data-testid="add-principal-btn" className="bg-slate-200 text-slate-900 hover:bg-slate-100">
          Add Principal
        </Button>
      </div>

      {principals.map((principal, index) => (
        <div key={principal.id} className="bg-slate-800 p-6 rounded-lg relative">
          <button
            onClick={() => removePrincipal(index)}
            className="absolute top-4 right-4 text-red-400 hover:text-red-300"
            data-testid={`remove-principal-${index}`}
          >
            ✕
          </button>

          <h4 className="text-lg font-medium text-slate-200 mb-6">Principal #{index + 1}</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-slate-200">First Name *</Label>
              <Input
                value={principal.firstName || ''}
                onChange={(e) => updatePrincipal(index, 'firstName', e.target.value)}
                className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                data-testid={`principal-${index}-firstName`}
              />
            </div>
            
            <div>
              <Label className="text-slate-200">Last Name *</Label>
              <Input
                value={principal.lastName || ''}
                onChange={(e) => updatePrincipal(index, 'lastName', e.target.value)}
                className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                data-testid={`principal-${index}-lastName`}
              />
            </div>
            
            <div>
              <Label className="text-slate-200">Nationality</Label>
              <Input
                value={principal.nationality || ''}
                onChange={(e) => updatePrincipal(index, 'nationality', e.target.value)}
                className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
              />
            </div>
            
            <div>
              <Label className="text-slate-200">Title *</Label>
              <Select value={principal.title || ''} onValueChange={(value) => updatePrincipal(index, 'title', value)}>
                <SelectTrigger className="mt-2 bg-slate-900 border-slate-700 text-slate-100">
                  <SelectValue placeholder="Select Title" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CEO">CEO</SelectItem>
                  <SelectItem value="CFO">CFO</SelectItem>
                  <SelectItem value="Owner">Owner</SelectItem>
                  <SelectItem value="Partner">Partner</SelectItem>
                  <SelectItem value="President">President</SelectItem>
                  <SelectItem value="Vice President">Vice President</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-200">Social Security Number *</Label>
              <MaskedInput
                value={principal.ssn || ''}
                onChange={(e) => updatePrincipal(index, 'ssn', e.target.value)}
                maskPattern="ssn"
                className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                placeholder="XXX-XX-XXXX"
              />
            </div>
            
            <div>
              <Label className="text-slate-200">Day of Birth *</Label>
              <Input
                type="date"
                value={principal.dayOfBirth || ''}
                onChange={(e) => updatePrincipal(index, 'dayOfBirth', e.target.value)}
                className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                placeholder="MM/DD/YYYY"
              />
            </div>

            <div>
              <Label className="text-slate-200">Drivers License Number</Label>
              <MaskedInput
                value={principal.driverLicense?.number || ''}
                onChange={(e) => updatePrincipalDriverLicense(index, 'number', e.target.value)}
                maskPattern="dl"
                className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
              />
            </div>
            
            <div>
              <Label className="text-slate-200">Driver License State</Label>
              <Input
                value={principal.driverLicense?.state || ''}
                onChange={(e) => updatePrincipalDriverLicense(index, 'state', e.target.value)}
                className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                placeholder="e.g., California"
              />
            </div>
            
            <div>
              <Label className="text-slate-200">Expiration Date</Label>
              <Input
                type="date"
                value={principal.driverLicense?.expiration || ''}
                onChange={(e) => updatePrincipalDriverLicense(index, 'expiration', e.target.value)}
                className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
              />
            </div>
          </div>

          <div className="mt-6">
            <h5 className="text-lg font-medium text-slate-200 mb-4">Address & Contact Information</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label className="text-slate-200">Address *</Label>
                <Input
                  value={principal.address?.street || ''}
                  onChange={(e) => updatePrincipalAddress(index, 'street', e.target.value)}
                  className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                />
              </div>
              
              <div>
                <Label className="text-slate-200">State *</Label>
                <Input
                  value={principal.address?.state || ''}
                  onChange={(e) => updatePrincipalAddress(index, 'state', e.target.value)}
                  className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                />
              </div>
              
              <div>
                <Label className="text-slate-200">Zip *</Label>
                <Input
                  value={principal.address?.zip || ''}
                  onChange={(e) => updatePrincipalAddress(index, 'zip', e.target.value)}
                  className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                />
              </div>
              
              <div>
                <Label className="text-slate-200">City *</Label>
                <Input
                  value={principal.address?.city || ''}
                  onChange={(e) => updatePrincipalAddress(index, 'city', e.target.value)}
                  className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                />
              </div>
              
              <div>
                <Label className="text-slate-200">Telephone Number *</Label>
                <Input
                  type="tel"
                  value={principal.phone || ''}
                  onChange={(e) => updatePrincipal(index, 'phone', e.target.value)}
                  className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                  placeholder="+1"
                />
              </div>
              
              <div>
                <Label className="text-slate-200">Email *</Label>
                <Input
                  type="email"
                  value={principal.email || ''}
                  onChange={(e) => updatePrincipal(index, 'email', e.target.value)}
                  className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                />
              </div>
              
              <div>
                <Label className="text-slate-200">Ownership Percentage *</Label>
                <Input
                  type="number"
                  value={principal.ownershipPercentage || ''}
                  onChange={(e) => updatePrincipal(index, 'ownershipPercentage', parseFloat(e.target.value))}
                  className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
                  min="0"
                  max="100"
                />
              </div>
              
              <div>
                <Label className="text-slate-200">Is Signer?</Label>
                <Select value={principal.isSigner || 'No'} onValueChange={(value) => updatePrincipal(index, 'isSigner', value)}>
                  <SelectTrigger className="mt-2 bg-slate-900 border-slate-700 text-slate-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-slate-200">Is Personal Guarantee?</Label>
                <Select value={principal.isPersonalGuarantee || 'No'} onValueChange={(value) => updatePrincipal(index, 'isPersonalGuarantee', value)}>
                  <SelectTrigger className="mt-2 bg-slate-900 border-slate-700 text-slate-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label className="text-slate-200">Is this the individual you want to list as management?</Label>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id={`management-yes-${index}`}
                      checked={principal.isManagement === 'Yes'}
                      onChange={() => updatePrincipal(index, 'isManagement', 'Yes')}
                      className="mr-2"
                    />
                    <Label htmlFor={`management-yes-${index}`} className="text-slate-300 cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id={`management-no-${index}`}
                      checked={principal.isManagement === 'No'}
                      onChange={() => updatePrincipal(index, 'isManagement', 'No')}
                      className="mr-2"
                    />
                    <Label htmlFor={`management-no-${index}`} className="text-slate-300 cursor-pointer">No</Label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-slate-900 p-4 rounded">
            <h5 className="text-md font-medium text-slate-200 mb-3">History Information</h5>
            <Label className="text-slate-200">Have you (your business or any principal) filed bankruptcy? *</Label>
            <div className="mt-3 space-y-2">
              <div className="flex items-center">
                <input type="radio" id={`bankruptcy-business-${index}`} name={`bankruptcy-${index}`} className="mr-2" />
                <Label htmlFor={`bankruptcy-business-${index}`} className="text-slate-300 cursor-pointer">Business Bankruptcy</Label>
              </div>
              <div className="flex items-center">
                <input type="radio" id={`bankruptcy-personal-${index}`} name={`bankruptcy-${index}`} className="mr-2" />
                <Label htmlFor={`bankruptcy-personal-${index}`} className="text-slate-300 cursor-pointer">Personal Bankruptcy</Label>
              </div>
              <div className="flex items-center">
                <input type="radio" id={`bankruptcy-never-${index}`} name={`bankruptcy-${index}`} className="mr-2" defaultChecked />
                <Label htmlFor={`bankruptcy-never-${index}`} className="text-slate-300 cursor-pointer">Never</Label>
              </div>
            </div>
          </div>
        </div>
      ))}

      {principals.length === 0 && (
        <div className="bg-slate-800 p-12 rounded-lg text-center">
          <p className="text-slate-400 mb-4">No principals added yet</p>
          <Button onClick={addPrincipal} data-testid="add-first-principal-btn" className="bg-slate-200 text-slate-900 hover:bg-slate-100">
            Add Your First Principal
          </Button>
        </div>
      )}
    </div>
  );
};

export default PrincipalStep;
